import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import useAuth from '../hooks/useAuth';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import axios from '../api/axios';
import { imageUrl } from '../utils/imageUrl';

const AdminHome = () => {
    const { setAuth } = useAuth();
    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();

    const [projects, setProjects] = useState([]);
    const [loadingProjects, setLoadingProjects] = useState(true);

    const [testimonials, setTestimonials] = useState([]);
    const [loadingTestimonials, setLoadingTestimonials] = useState(true);
    const [tForm, setTForm] = useState({ author: '', testimonial: '' });
    const [tSaving, setTSaving] = useState(false);
    const [tError, setTError] = useState('');

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const [projRes, testRes] = await Promise.all([
                    axiosPrivate.get('/gallery'),
                    axiosPrivate.get('/testimonials'),
                ]);
                if (mounted) {
                    setProjects(projRes.data.projects || []);
                    setTestimonials(testRes.data.testimonials || []);
                }
            } catch { /* RequireAuth handles unauth */ } finally {
                if (mounted) {
                    setLoadingProjects(false);
                    setLoadingTestimonials(false);
                }
            }
        };
        load();
        return () => { mounted = false; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLogout = async () => {
        try { await axios.post('/auth/logout', {}, { withCredentials: true }); } catch { /* ignore */ }
        setAuth({});
        navigate('/adminlogin');
    };

    const handleDeleteProject = async (id) => {
        if (!window.confirm('Delete this project? This cannot be undone.')) return;
        try {
            await axiosPrivate.delete(`/gallery/${id}`);
            setProjects(prev => prev.filter(p => p._id !== id));
        } catch { alert('Failed to delete project.'); }
    };

    const handleAddTestimonial = async (e) => {
        e.preventDefault();
        if (!tForm.author.trim() || !tForm.testimonial.trim()) {
            setTError('Both fields are required.');
            return;
        }
        setTSaving(true);
        setTError('');
        try {
            const res = await axiosPrivate.post('/testimonials', {
                author: tForm.author.trim(),
                testimonial: tForm.testimonial.trim(),
                order: testimonials.length,
            });
            setTestimonials(prev => [...prev, res.data.testimonial]);
            setTForm({ author: '', testimonial: '' });
        } catch (err) {
            setTError(err?.response?.data?.msg || 'Failed to save.');
        } finally {
            setTSaving(false);
        }
    };

    const handleDeleteTestimonial = async (id) => {
        if (!window.confirm('Delete this testimonial?')) return;
        try {
            await axiosPrivate.delete(`/testimonials/${id}`);
            setTestimonials(prev => prev.filter(t => t._id !== id));
        } catch { alert('Failed to delete testimonial.'); }
    };

    const handleToggleVisible = async (t) => {
        try {
            const res = await axiosPrivate.patch(`/testimonials/${t._id}`, { visible: !t.visible });
            setTestimonials(prev => prev.map(x => x._id === t._id ? res.data.testimonial : x));
        } catch { alert('Failed to update.'); }
    };

    return (
        <div className="min-h-screen bg-stone-50">
            {/* Header */}
            <header className="bg-white border-b border-stone-200 px-8 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <img src="/images/Logo.png" alt="Kia Kaha" className="h-9 w-auto" />
                    <div>
                        <p className="font-serif text-lg text-stone-800">Kia Kaha</p>
                        <p className="font-sans text-xs tracking-widest uppercase text-stone-400">Admin Dashboard</p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <Link to="/" className="font-sans text-xs tracking-widest uppercase text-stone-400 hover:text-jade-500 transition-colors">
                        View Site
                    </Link>
                    <button onClick={handleLogout} className="font-sans text-xs tracking-widest uppercase text-stone-400 hover:text-red-500 transition-colors">
                        Sign Out
                    </button>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-8 py-12 space-y-16">

                {/* ── Projects ── */}
                <div>
                    <div className="flex items-center justify-between mb-10">
                        <div>
                            <h1 className="font-serif text-4xl font-light text-stone-800">Projects</h1>
                            <div className="w-8 h-px bg-jade-400 mt-3" />
                        </div>
                        <Link to="/admin/projects/new" className="btn-jade">+ New Project</Link>
                    </div>

                    {loadingProjects && (
                        <div className="flex justify-center py-16">
                            <div className="w-7 h-7 border-2 border-jade-400 border-t-transparent rounded-full animate-spin" />
                        </div>
                    )}

                    {!loadingProjects && projects.length === 0 && (
                        <div className="text-center py-16 border border-dashed border-stone-300">
                            <p className="font-sans text-stone-400 text-sm mb-6">No projects yet.</p>
                            <Link to="/admin/projects/new" className="btn-outline">Create Your First Project</Link>
                        </div>
                    )}

                    {!loadingProjects && projects.length > 0 && (
                        <div className="space-y-4">
                            {projects.map((project) => (
                                <div key={project._id} className="bg-white border border-stone-200 flex items-center gap-6 p-4 hover:border-jade-300 transition-colors duration-200">
                                    <div className="w-20 h-16 bg-stone-100 flex-shrink-0 overflow-hidden">
                                        {project.coverImage ? (
                                            <img src={imageUrl(project.coverImage)} alt={project.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <span className="font-sans text-xs text-stone-300">No img</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-serif text-lg text-stone-800 truncate">{project.name}</p>
                                        {project.location && <p className="font-sans text-xs tracking-widest uppercase text-stone-400 mt-0.5">{project.location}</p>}
                                        <p className="font-sans text-xs text-stone-400 mt-1">{project.images?.length || 0} image{project.images?.length !== 1 ? 's' : ''}</p>
                                    </div>
                                    <div className="flex items-center gap-4 flex-shrink-0">
                                        <Link to={`/gallery/${project._id}`} className="font-sans text-xs tracking-widest uppercase text-stone-400 hover:text-jade-500 transition-colors" target="_blank" rel="noreferrer">View</Link>
                                        <Link to={`/admin/projects/${project._id}`} className="font-sans text-xs tracking-widest uppercase text-stone-400 hover:text-jade-500 transition-colors">Edit</Link>
                                        <button onClick={() => handleDeleteProject(project._id)} className="font-sans text-xs tracking-widest uppercase text-stone-300 hover:text-red-400 transition-colors">Delete</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Testimonials ── */}
                <div>
                    <div className="mb-10">
                        <h2 className="font-serif text-4xl font-light text-stone-800">Testimonials</h2>
                        <div className="w-8 h-px bg-jade-400 mt-3" />
                    </div>

                    {/* Existing testimonials */}
                    {loadingTestimonials ? (
                        <div className="flex justify-center py-8">
                            <div className="w-7 h-7 border-2 border-jade-400 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <div className="space-y-3 mb-10">
                            {testimonials.length === 0 && (
                                <p className="font-sans text-stone-400 text-sm py-4">No testimonials yet.</p>
                            )}
                            {testimonials.map((t) => (
                                <div key={t._id} className={`bg-white border p-5 flex gap-4 transition-colors ${t.visible ? 'border-stone-200' : 'border-stone-100 opacity-50'}`}>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-serif text-base text-stone-700 leading-relaxed mb-2">{t.testimonial}</p>
                                        <p className="font-sans text-xs tracking-widest uppercase text-jade-500">— {t.author}</p>
                                    </div>
                                    <div className="flex items-start gap-3 flex-shrink-0">
                                        <button
                                            onClick={() => handleToggleVisible(t)}
                                            className={`font-sans text-xs tracking-widest uppercase transition-colors ${t.visible ? 'text-jade-500 hover:text-stone-400' : 'text-stone-300 hover:text-jade-500'}`}
                                        >
                                            {t.visible ? 'Hide' : 'Show'}
                                        </button>
                                        <button onClick={() => handleDeleteTestimonial(t._id)} className="text-stone-300 hover:text-red-400 transition-colors p-1">
                                            <FaTimes className="w-3 h-3" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Add new testimonial */}
                    <div className="bg-white border border-stone-200 p-8">
                        <h3 className="font-sans text-xs tracking-widest uppercase text-stone-400 mb-6">Add Testimonial</h3>
                        {tError && <p className="font-sans text-sm text-red-500 mb-4">{tError}</p>}
                        <form onSubmit={handleAddTestimonial} className="space-y-5">
                            <div>
                                <label className="block font-sans text-xs tracking-widest uppercase text-stone-400 mb-2">Client Name *</label>
                                <input
                                    type="text"
                                    value={tForm.author}
                                    onChange={e => setTForm(p => ({ ...p, author: e.target.value }))}
                                    className="w-full border-b border-stone-300 bg-transparent py-2 font-sans text-stone-700 focus:outline-none focus:border-jade-500 transition-colors"
                                    placeholder="e.g. John & Jane Smith"
                                />
                            </div>
                            <div>
                                <label className="block font-sans text-xs tracking-widest uppercase text-stone-400 mb-2">Testimonial *</label>
                                <textarea
                                    value={tForm.testimonial}
                                    onChange={e => setTForm(p => ({ ...p, testimonial: e.target.value }))}
                                    rows={4}
                                    className="w-full border-b border-stone-300 bg-transparent py-2 font-sans text-stone-700 focus:outline-none focus:border-jade-500 transition-colors resize-none"
                                    placeholder="What did the client say?"
                                />
                            </div>
                            <button type="submit" disabled={tSaving} className="btn-jade disabled:opacity-50">
                                {tSaving ? 'Saving…' : 'Add Testimonial'}
                            </button>
                        </form>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminHome;
